/**
 * TokenManager - Gerenciamento unificado de tokens
 *
 * Fonte única de verdade para todos os tokens da aplicação:
 * - accessToken (JWT principal)
 * - refreshToken (renovação)
 * - ssoToken (tokens de curta duração para /tools/*)
 * - baseBearer (token base para renovação SSO)
 *
 * Recursos:
 * - Dual-write: escreve em chaves novas E antigas durante migração
 * - Auto-migração: migra automaticamente chaves legadas
 * - Memory cache: fallback em memória para performance
 * - Logs de migração: rastreamento de migrações para debug
 */

import { sendToTerminal, logger } from "../terminalLogger";
import {
	getAccessTokenSync,
	getRefreshTokenSync,
	getUserDataSync,
	getSsoTokenSync,
	getSsoExpSync,
	getBaseBearerSync,
	_setMirror,
} from "./tokenMirror";

/**
 * Patch do evento global de mudança de auth.
 * Apenas as chaves presentes são propagadas — undefined ≠ null.
 */
type AuthUpdatedPatch = {
	accessToken?: string | null;
	refreshToken?: string | null;
	userData?: any | null;
	ssoToken?: string | null;
	ssoExp?: string | null;
	baseBearer?: string | null;
};

/**
 * Dispatcher do evento global de mudança de auth.
 * AuthContext escuta isto para manter seu estado em sincronia.
 *
 * Para qualquer campo gerenciado pelo AuthContext (accessToken, refreshToken,
 * userData), este evento é a forma canônica de propagar mudanças — incluindo
 * logout, em que devemos enviar o valor `null`.
 */
function dispatchAuthUpdated(patch: AuthUpdatedPatch): void {
	try {
		// Mirror é atualizado imediatamente para garantir leitura síncrona
		// (api.ts, fetch interceptors, socketService) mesmo antes do AuthContext
		// re-renderizar.
		_setMirror(patch);
		window.dispatchEvent(
			new CustomEvent("auth:updated", { detail: patch }),
		);
	} catch {
		// silencioso: ambiente sem window (SSR/teste) ou CustomEvent indisponível
	}
}

function persistAccessToken(token: string): void {
	dualWrite(KEYS.ACCESS_TOKEN, LEGACY_KEYS.ACCESS_TOKEN, token);
}

function persistRefreshToken(token: string): void {
	safeSetItem(KEYS.REFRESH_TOKEN, token);
}

function persistUserData(userData: any): boolean {
	try {
		const json = JSON.stringify(userData);
		dualWrite(KEYS.USER_DATA, LEGACY_KEYS.USER_DATA, json);
		return true;
	} catch (e) {
		logger.warn("⚠️ Falha ao salvar userData:", e);
		return false;
	}
}

// ========================
// CONFIGURAÇÃO
// ========================

/**
 * Feature flags para controlar migração gradual
 */
export const TOKEN_FEATURE_FLAGS = {
	DUAL_WRITE_ENABLED: true, // Escreve em chaves novas + antigas
	LEGACY_READ_ENABLED: true, // Permite fallback para chaves antigas
	MIGRATION_LOGGING: true, // Logs de migração automática
};

// ========================
// CHAVES DE STORAGE
// ========================

/**
 * Chaves padronizadas (novo padrão cdr:*)
 */
const KEYS = {
	ACCESS_TOKEN: "cdr:auth:accessToken",
	REFRESH_TOKEN: "cdr:auth:refreshToken",
	USER_DATA: "cdr:auth:userData",
	SSO_TOKEN: "cdr:tools_sso:token",
	SSO_EXP: "cdr:tools_sso:exp",
	BASE_BEARER: "cdr:tools_sso:base_bearer",
} as const;

/**
 * Chaves legadas (para migração)
 */
const LEGACY_KEYS = {
	ACCESS_TOKEN: ["accessToken"],
	USER_DATA: ["userData"],
	SSO_TOKEN: ["tools_sso_token"],
	// Não havia chaves legadas para refresh, exp e base_bearer
} as const;

// ========================
// MEMÓRIA IN-MEMORY
// ========================

/**
 * O cache `memory` foi totalmente removido.
 *
 * Todos os tokens (`accessToken`, `refreshToken`, `userData`, `ssoToken`,
 * `ssoExp`, `baseBearer`) vivem no AuthContext (fonte única de verdade).
 * O `tokenMirror` é o cache síncrono para código não-React, alimentado
 * exclusivamente pelo Provider e pelos setters deste TokenManager (que
 * dispatcham `auth:updated`).
 */

// ========================
// HELPERS INTERNOS
// ========================

/**
 * Tenta ler de sessionStorage com fallback seguro
 */
function safeGetItem(key: string): string | null {
	try {
		return sessionStorage.getItem(key);
	} catch {
		return null;
	}
}

/**
 * Tenta escrever em sessionStorage com fallback seguro
 */
function safeSetItem(key: string, value: string): void {
	try {
		sessionStorage.setItem(key, value);
	} catch (e) {
		logger.warn(`⚠️ Falha ao salvar em sessionStorage: ${key}`, e);
	}
}

/**
 * Tenta remover de sessionStorage com fallback seguro
 */
function safeRemoveItem(key: string): void {
	try {
		sessionStorage.removeItem(key);
	} catch (e) {
		logger.warn(`⚠️ Falha ao remover de sessionStorage: ${key}`, e);
	}
}

/**
 * Migra uma chave legada para a nova se existir
 * @returns valor migrado ou null
 */
function migrateIfNeeded(newKey: string, legacyKeys: string[]): string | null {
	if (!TOKEN_FEATURE_FLAGS.LEGACY_READ_ENABLED) {
		return null;
	}

	for (const legacyKey of legacyKeys) {
		const value = safeGetItem(legacyKey);

		if (value) {
			// Migrar para nova chave
			safeSetItem(newKey, value);

			// Log de migração
			if (TOKEN_FEATURE_FLAGS.MIGRATION_LOGGING) {
				try {
					sendToTerminal("🔄 [TOKEN_MANAGER:MIGRATION]", {
						from: legacyKey,
						to: newKey,
						timestamp: new Date().toISOString(),
					});
				} catch {}
			}

			// Remover chave legada após migração bem-sucedida
			safeRemoveItem(legacyKey);

			return value;
		}
	}

	return null;
}

/**
 * Escreve em múltiplas chaves (dual-write)
 */
function dualWrite(newKey: string, legacyKeys: string[], value: string): void {
	// Sempre escreve na chave nova
	safeSetItem(newKey, value);

	// Escreve nas chaves legadas se dual-write ativo
	if (TOKEN_FEATURE_FLAGS.DUAL_WRITE_ENABLED) {
		legacyKeys.forEach((legacyKey) => {
			safeSetItem(legacyKey, value);
		});

		// Log removido - dual-write é operação rotineira e não precisa de log
	}
}

/**
 * Remove de múltiplas chaves (dual-delete)
 */
function dualRemove(newKey: string, legacyKeys: string[]): void {
	safeRemoveItem(newKey);

	if (TOKEN_FEATURE_FLAGS.DUAL_WRITE_ENABLED) {
		legacyKeys.forEach(safeRemoveItem);
	}
}

// ========================
// API PÚBLICA
// ========================

export class TokenManager {
	// ========================
	// ACCESS TOKEN
	// ========================
	//
	// O accessToken é gerenciado pelo AuthContext (fonte única de verdade).
	// Os métodos abaixo são proxies que:
	//   - escrevem em sessionStorage (persistência) + dispatcham auth:updated
	//   - leem do tokenMirror (síncrono, atualizado pelo AuthContext)
	//
	// Manter os nomes mantém compatibilidade com ~43 callers existentes.

	/**
	 * Define o accessToken. Persiste em sessionStorage e notifica o AuthContext
	 * via evento global `auth:updated`.
	 */
	static setAccessToken(token: string): void {
		persistAccessToken(token);
		dispatchAuthUpdated({ accessToken: token });
	}

	/**
	 * Obtém o accessToken.
	 * Lê do mirror (cache síncrono atualizado pelo AuthContext). Em primeiro
	 * acesso antes do Provider montar, o mirror faz lazy-load do sessionStorage
	 * com migração automática da chave legada.
	 */
	static getAccessToken(): string | null {
		return getAccessTokenSync();
	}

	/**
	 * Atualiza apenas o accessToken (usado após refresh)
	 */
	static updateAccessToken(token: string): void {
		this.setAccessToken(token);
	}

	// ========================
	// REFRESH TOKEN
	// ========================

	/**
	 * Define o refreshToken. Persiste em sessionStorage e notifica o
	 * AuthContext via evento global `auth:updated`.
	 */
	static setRefreshToken(token: string): void {
		persistRefreshToken(token);
		dispatchAuthUpdated({ refreshToken: token });
	}

	/**
	 * Obtém o refreshToken.
	 * Lê do mirror (cache síncrono atualizado pelo AuthContext).
	 */
	static getRefreshToken(): string | null {
		return getRefreshTokenSync();
	}

	// ========================
	// USER DATA
	// ========================

	/**
	 * Define os dados do usuário. Persiste em sessionStorage (JSON) e notifica
	 * o AuthContext via evento global `auth:updated`.
	 */
	static setUserData(userData: any): void {
		if (!persistUserData(userData)) {
			return;
		}
		dispatchAuthUpdated({ userData });
	}

	/**
	 * Obtém os dados do usuário.
	 * Lê do mirror (objeto parseado, atualizado pelo AuthContext).
	 */
	static getUserData(): any | null {
		const cached = getUserDataSync();
		if (cached) return cached;

		// Fallback: se nada foi populado no mirror ainda, tenta migrar chaves legadas.
		const migrated = migrateIfNeeded(KEYS.USER_DATA, LEGACY_KEYS.USER_DATA);
		if (migrated) {
			try {
				return JSON.parse(migrated);
			} catch {
				return null;
			}
		}

		return null;
	}

	// ========================
	// SSO TOKEN (Tools)
	// ========================

	/**
	 * Define o token SSO de curta duração. Persiste em sessionStorage e
	 * notifica o AuthContext via evento `auth:updated`.
	 */
	static setSsoToken(token: string, expiresAtIso?: string): void {
		safeSetItem(KEYS.SSO_TOKEN, token);
		if (expiresAtIso) {
			safeSetItem(KEYS.SSO_EXP, expiresAtIso);
		}

		// Atualiza mirror + propaga para AuthContext.
		// Inclui `ssoExp` no patch só quando recebido — undefined ≠ null.
		const patch: AuthUpdatedPatch = { ssoToken: token };
		if (expiresAtIso) patch.ssoExp = expiresAtIso;
		dispatchAuthUpdated(patch);

		if (TOKEN_FEATURE_FLAGS.MIGRATION_LOGGING) {
			try {
				sendToTerminal("🔑 [TOKEN_MANAGER:SSO_SET]", {
					hasSsoData: true,
					hasExpiration: !!expiresAtIso,
					timestamp: new Date().toISOString(),
				});
			} catch {}
		}
	}

	/**
	 * Obtém o token SSO.
	 * Lê do mirror (cache síncrono atualizado pelo AuthContext).
	 */
	static getSsoToken(): string | null {
		return getSsoTokenSync();
	}

	/**
	 * Obtém a data de expiração do SSO token.
	 * Lê do mirror (cache síncrono atualizado pelo AuthContext).
	 */
	static getSsoExpiration(): string | null {
		return getSsoExpSync();
	}

	// ========================
	// BASE BEARER (SSO Refresh)
	// ========================

	/**
	 * Define o base bearer (token para renovar SSO). Persiste em sessionStorage
	 * e notifica o AuthContext via evento `auth:updated`.
	 */
	static setBaseBearer(token: string): void {
		safeSetItem(KEYS.BASE_BEARER, token);
		dispatchAuthUpdated({ baseBearer: token });
	}

	/**
	 * Obtém o base bearer.
	 * Lê do mirror (cache síncrono atualizado pelo AuthContext).
	 */
	static getBaseBearer(): string | null {
		return getBaseBearerSync();
	}

	// ========================
	// OPERAÇÕES GERAIS
	// ========================

	/**
	 * Define todos os tokens após login
	 */
	static setTokens(
		accessToken: string,
		refreshToken?: string,
		userData?: any,
	): void {
		persistAccessToken(accessToken);

		const patch: AuthUpdatedPatch = {
			accessToken,
		};

		if (refreshToken) {
			persistRefreshToken(refreshToken);
			patch.refreshToken = refreshToken;
		}

		if (userData) {
			if (persistUserData(userData)) {
				patch.userData = userData;
			}
		}

		dispatchAuthUpdated(patch);
	}

	/**
	 * Limpa todos os tokens (logout)
	 */
	static clearTokens(): void {
		// Limpar storage (chaves novas + legadas).
		dualRemove(KEYS.ACCESS_TOKEN, LEGACY_KEYS.ACCESS_TOKEN);
		dualRemove(KEYS.USER_DATA, LEGACY_KEYS.USER_DATA);
		safeRemoveItem(KEYS.REFRESH_TOKEN);
		safeRemoveItem(KEYS.SSO_TOKEN);
		safeRemoveItem(KEYS.SSO_EXP);
		safeRemoveItem(KEYS.BASE_BEARER);

		// Propagar para o AuthContext que TODOS os campos auth foram removidos.
		// Sem isto, hooks (useAccessToken, useRefreshToken, useUserData,
		// useSsoToken, useBaseBearer) só veriam o logout no próximo evento.
		dispatchAuthUpdated({
			accessToken: null,
			refreshToken: null,
			userData: null,
			ssoToken: null,
			ssoExp: null,
			baseBearer: null,
		});

		if (TOKEN_FEATURE_FLAGS.MIGRATION_LOGGING) {
			try {
				sendToTerminal("🧹 [TOKEN_MANAGER:CLEAR_ALL]", {
					timestamp: new Date().toISOString(),
				});
			} catch {}
		}
	}

	/**
	 * Verifica se existe algum token válido
	 */
	static hasValidToken(): boolean {
		return !!this.getAccessToken();
	}

	/**
	 * Verifica se existe uma sessão ativa considerando todas as fontes de
	 * sessão (access, sso ou baseBearer). Em embedded/SSO o accessToken pode
	 * estar transitoriamente vazio enquanto ssoToken/baseBearer seguram a
	 * sessão — este é o "single source of truth" real da sessão.
	 */
	static hasActiveSession(): boolean {
		return !!(this.getAccessToken() || this.getSsoToken() || this.getBaseBearer());
	}

	/**
	 * Exporta snapshot de todos os tokens (para debug)
	 */
	static getSnapshot(): {
		accessToken: string | null;
		refreshToken: string | null;
		ssoToken: string | null;
		baseBearer: string | null;
		userData: any | null;
	} {
		return {
			accessToken: this.getAccessToken(),
			refreshToken: this.getRefreshToken(),
			ssoToken: this.getSsoToken(),
			baseBearer: this.getBaseBearer(),
			userData: this.getUserData(),
		};
	}
}

// Export singleton para compatibilidade
export default TokenManager;
